import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, X } from "lucide-react";
import { businessInfo } from "../mock";

// Sticky bottom CTA — appears after 30% scroll on landing pages.
// Dismissible per session; topic-aware so the contact form pre-fills the message.
const StickyCTA = ({ topicSlug, label = "Free Claim Review" }) => {
  const [visible, setVisible] = useState(false);
  // Lazy-init from sessionStorage to satisfy the no-set-state-in-effect rule
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("cb_sticky_dismissed") === "1";
  });

  useEffect(() => {
    if (dismissed) return undefined;
    const handleScroll = () => {
      const doc = document.documentElement;
      const scrolled = window.scrollY;
      const total = doc.scrollHeight - window.innerHeight;
      const pct = total > 0 ? scrolled / total : 0;
      setVisible(pct >= 0.3);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [dismissed]);

  if (dismissed || !visible) return null;

  const ctaHref = topicSlug
    ? `/?topic=${encodeURIComponent(topicSlug)}#contact`
    : "/#contact";

  return (
    <div
      role="region"
      aria-label="Free Florida claim review"
      data-testid="sticky-cta"
      className="fixed inset-x-0 bottom-0 z-40 border-t-2 border-[#E11D26] bg-black/95 backdrop-blur-sm shadow-[0_-12px_30px_rgba(225,29,38,0.25)] animate-in slide-in-from-bottom duration-300"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 sm:gap-5">
        <div className="hidden sm:flex h-10 w-10 rounded-full bg-[#E11D26]/20 border border-[#E11D26]/50 items-center justify-center text-[#E11D26] shrink-0">
          <Phone className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-white font-black uppercase text-sm sm:text-base tracking-wide leading-tight truncate"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            <span className="sm:hidden">Free Florida claim review</span>
            <span className="hidden sm:inline">
              Don&apos;t accept their denial. Get a free review.
            </span>
          </div>
          <div className="text-white/60 text-[11px] sm:text-xs hidden sm:block">
            No upfront cost. No win, no fee. Reply within 1 business day.
          </div>
        </div>
        <Link
          to={ctaHref}
          data-testid="sticky-cta-primary"
          className="shrink-0 inline-flex items-center gap-2 bg-[#E11D26] hover:bg-[#c11820] text-white font-bold uppercase tracking-wider px-4 sm:px-5 py-2.5 sm:py-3 text-xs sm:text-sm transition-colors"
        >
          <span className="whitespace-nowrap">{label}</span>
          <ArrowRight className="h-4 w-4" />
        </Link>
        <a
          href={`tel:${businessInfo.phoneRaw}`}
          data-testid="sticky-cta-phone"
          className="hidden md:inline-flex items-center gap-2 bg-transparent border border-white/30 text-white hover:bg-white hover:text-black font-bold uppercase tracking-wider px-4 py-3 text-xs transition-colors"
        >
          <Phone className="h-3.5 w-3.5" />
          {businessInfo.phone}
        </a>
        <button
          onClick={() => {
            sessionStorage.setItem("cb_sticky_dismissed", "1");
            setDismissed(true);
          }}
          data-testid="sticky-cta-dismiss"
          aria-label="Dismiss"
          className="shrink-0 h-8 w-8 sm:h-9 sm:w-9 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default StickyCTA;
